import { RoutesFilters } from "./../../shared-types/src/api/RoutesFilters";
import { processParams } from "@repo/helpers";
import {
  AppPathResponse,
  AppSettingsResponse,
  CalculateRequest,
  FacilityCacheModel,
  FacilityDetailsResponse,
  FacilitySummaryResponse,
  GeoJsonResponse,
  GroupedPathsOverviewResponse,
  MapFilters,
  PathDetailResponse,
  PathOverviewResponse,
  PathResponse,
  SearchParams,
} from "@repo/shared-types";
import { FetchError, unwrap } from "./unwrap";
import { Language } from "@repo/i18n";

export { FetchError };

export class ApiClient {
  constructor(
    private readonly _baseUrl: string,
    private readonly _language: Language
  ) {}

  async getMapFilters(params: SearchParams = {}) {
    return this.execute<MapFilters>(
      `${this._baseUrl}/api/map/filters?${processParams(params).toString()}`
    );
  }

  async getEditableMapFilters(params: SearchParams = {}) {
    return this.execute<MapFilters>(
      `${this._baseUrl}/api/v2/map/filters?${processParams(params).toString()}`
    );
  }

  async getPathsFilters() {
    return this.execute<RoutesFilters>(
      `${this._baseUrl}/api/map/paths/overview/filters`
    );
  }

  getPathGPXUrl(id: string, variant: "reverse" | "base") {
    return `${this._baseUrl}/api/gpx/${variant}/${id}`;
  }

  getPathGPXUrlV2(id: string, variant: "reverse" | "base") {
    return `${this._baseUrl}/api/v2/gpx/${variant}/${id}`;
  }

  async getPathGPX(id: string) {
    const url = this.getPathGPXUrl(id, "base");
    return this.execute<Buffer>(url);
  }

  async getPathGPXV2(id: string) {
    const url = this.getPathGPXUrlV2(id, "base");
    return this.execute<Buffer>(url);
  }

  async getReversePathGPX(id: string) {
    const url = this.getPathGPXUrl(id, "reverse");
    return this.execute<Buffer>(url);
  }

  async getReversePathGPXV2(id: string) {
    const url = this.getPathGPXUrlV2(id, "reverse");
    return this.execute<Buffer>(url);
  }

  getPathsGPXUrl(variant: "reverse" | "base", type: "hiking" | "biking") {
    return `${this._baseUrl}/api/gpx/${variant}?type=${type}`;
  }

  getPathsGPXUrlV2(variant: "reverse" | "base", type: "hiking" | "biking") {
    return `${this._baseUrl}/api/gpx/${variant}?type=${type}`;
  }

  async getPathsGPX(variant: "reverse" | "base", type: "hiking" | "biking") {
    const url = this.getPathsGPXUrl(variant, type);
    return this.execute<Buffer>(url);
  }

  async getPathsGPXV2(variant: "reverse" | "base", type: "hiking" | "biking") {
    const url = this.getPathsGPXUrl(variant, type);
    return this.execute<Buffer>(url);
  }

  async getPathsOverview() {
    return this.execute<GroupedPathsOverviewResponse[]>(
      `${this._baseUrl}/api/v2/map/paths/overview`
    );
  }

  async getPathOverview(id: string) {
    return this.execute<PathOverviewResponse>(
      `${this._baseUrl}/api/map/paths/overview/${id}`
    );
  }

  async getPathDetail(slug: string) {
    return this.execute<PathDetailResponse>(
      `${this._baseUrl}/api/map/paths/slug/${slug}`
    );
  }

  async getPathDetailByID(id: string) {
    return this.execute<PathDetailResponse>(
      `${this._baseUrl}/api/map/paths/id/${id}`
    );
  }

  async searchMapPaths(params: SearchParams = {}) {
    return this.execute<Omit<PathResponse, "geoJson">[]>(
      `${this._baseUrl}/api/map/paths?${processParams(params).toString()}`
    );
  }

  async getGeoJson() {
    return this.execute<GeoJsonResponse[]>(`${this._baseUrl}/api/map/geojson`);
  }

  getPathsGeojsons() {
    return this.execute<AppPathResponse[]>(
      `${this._baseUrl}/api/v2/map/geojson/mobile`
    );
  }

  async getGeoJsonV2() {
    return this.execute<GeoJsonResponse[]>(
      `${this._baseUrl}/api/v2/map/geojson`
    );
  }

  async searchFacilities(params: SearchParams = {}) {
    return this.execute<FacilitySummaryResponse[]>(
      `${this._baseUrl}/api/map/facilities?${processParams(params).toString()}`
    );
  }

  async searchFacilitiesV2(params: SearchParams = {}) {
    return this.execute<FacilitySummaryResponse[]>(
      `${this._baseUrl}/api/v2/map/facilities?${processParams(params).toString()}`
    );
  }

  async searchFacility(id: string) {
    return this.execute<FacilityDetailsResponse | null>(
      `${this._baseUrl}/api/map/facilities/${id}`
    );
  }

  async searchFacilityV2(id: string) {
    return this.execute<FacilityDetailsResponse | null>(
      `${this._baseUrl}/api/v2/map/facilities/${id}`
    );
  }

  async getAppSettings() {
    return this.execute<AppSettingsResponse | null>(
      `${this._baseUrl}/api/app/app-settings`
    );
  }

  async calculateAppFacilities(requestBody: CalculateRequest) {
    return this.execute<FacilityCacheModel[]>(
      `${this._baseUrl}/api/facilities/initialize/calculate`,
      {
        method: "POST",
        body: requestBody,
        authorize: true,
      }
    );
  }

  private async execute<Result>(url: string, options?: RequestOptions) {
    const { method = "GET", body, authorize } = options || {};

    const headers: HeadersInit = {
      "Accept-Language": this._language,
      "Content-Type": "application/json",
    };

    if (authorize) {
      headers["Authorization"] = `Bearer ${process.env.CRON_SECRET}`;
    }
    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }
    const res = await fetch(url, fetchOptions);
    return unwrap<Result>(res);
  }
}

interface RequestOptions {
  method?: string;
  body?: any;
  authorize?: boolean;
}
